import {
  axiosDeleteRequest,
  axiosGetRequest,
  axiosOpenPatchRequest,
  axiosPatchRequest,
  axiosPostRequest,
  axiosPutRequest,
  getCommonRoute,
  getIdentityRoute,
  getReferenceRoute,
  getVerificationEmailIdentityRoute,
} from '../api';
import { EmailsDto, UpdateMemberDto, UpdateUserDto } from './dtos';
import {
  Avatar,
  Carepoints,
  Country,
  Member,
  MemberInfo,
  MemberPreference,
  Membership,
  PreferenceType,
  User,
} from './model';
import { CarepointsReward } from './model/CarepointsReward';
import { VerifyEmailDto } from './model/VerifyEmailDto';

const addRoomIdToCall = (roomId?: string) => {
  return roomId ? `/${roomId}` : '';
};

const getFollowAuthorRoute = () =>
  `${getIdentityRoute()}/member/following/story`;

export const getUserCall = () => {
  return axiosGetRequest<User>(`${getIdentityRoute()}/user`);
};

export const updateUserCall = (user: UpdateUserDto) => {
  return axiosPutRequest<UpdateUserDto, User>(
    `${getIdentityRoute()}/user`,
    user
  );
};

export const getMemberCall = () => {
  return axiosGetRequest<Member>(`${getIdentityRoute()}/member`);
};

export const deleteAccountCall = () => {
  return axiosDeleteRequest<void, void>(`${getIdentityRoute()}/member/deleteaccountanddata`);
};

export const getMembershipCall = () => {
  return axiosGetRequest<Membership[]>(`${getIdentityRoute()}/membership`);
};

export const updateMemberCall = (member: UpdateMemberDto) => {
  return axiosPatchRequest<UpdateMemberDto, Member>(
    `${getIdentityRoute()}/member`,
    member
  );
};

export const updateMembershipCall = (membership: Membership) => {
  return axiosPatchRequest<Membership, Membership[]>(
    `${getIdentityRoute()}/member/membership`,
    membership
  );
};

export const getCountriesCall = () => {
  return axiosGetRequest<Country[]>(`${getReferenceRoute()}/countries`);
};

export const getCarepointsCall = () => {
  return axiosGetRequest<Carepoints[]>(`${getIdentityRoute()}/carepoints`);
};

export const getCarepointsRewardsCall = () => {
  return axiosGetRequest<CarepointsReward[]>(
    `${getReferenceRoute()}/carepoints`
  );
};

export const getAvatarsCall = () => {
  return axiosGetRequest<Avatar[]>(`${getIdentityRoute()}/avatars`);
};

export const getMemberInfoCall = (memberId: string, roomId?: string) => {
  return axiosGetRequest<MemberInfo>(
    `${getIdentityRoute()}/member/${memberId}/info/room${addRoomIdToCall(
      roomId
    )}`
  );
};

export const blockMemberCall = (memberId: string, roomId?: string) => {
  return axiosPatchRequest<string, void>(
    `${getIdentityRoute()}/member/${memberId}/block/room${addRoomIdToCall(
      roomId
    )}`
  );
};

export const unblockMemberCall = (memberId: string, roomId?: string) => {
  return axiosPatchRequest<string, void>(
    `${getIdentityRoute()}/member/${memberId}/unblock/room${addRoomIdToCall(
      roomId
    )}`
  );
};

export const insertFollowingMemberCall = (followingMemberId: string) => {
  return axiosPatchRequest(
    `${getFollowAuthorRoute()}/insert/${followingMemberId}`
  );
};

export const removeFollowingMemberCall = (followingMemberId: string) => {
  return axiosPatchRequest(
    `${getFollowAuthorRoute()}/remove/${followingMemberId}`
  );
};

export const fetchTopicsPreferencesCall = (type: PreferenceType) => {
  return axiosGetRequest<MemberPreference>(
    `${getIdentityRoute()}/member/preferences/${type}`
  );
};

export const fetchPreferencesCall = () => {
  return axiosGetRequest<MemberPreference[]>(
    `${getIdentityRoute()}/member/preferences/*`
  );
};

export const fetchJourneyRelationshipsCall = () => {
  return axiosGetRequest<string[]>(
    `${getIdentityRoute()}/member/relationshiptypes`
  );
};

export const updateTopicsPreferencesCall = (preference: MemberPreference) => {
  return axiosPatchRequest<MemberPreference, MemberPreference[]>(
    `${getIdentityRoute()}/member/preferences`,
    preference
  );
};

export const updatePreferencesCall = (
  memberPreferences: MemberPreference[]
) => {
  return axiosPatchRequest<MemberPreference[], MemberPreference[]>(
    `${getIdentityRoute()}/member/preferences`,
    memberPreferences
  );
};

export const reportMemberCall = (memberId: string, roomId?: string) => {
  return axiosPatchRequest(
    `${getIdentityRoute()}/member/${memberId}/report/room${addRoomIdToCall(
      roomId
    )}`
  );
};

export const getBlockedMembersCall = (numrecs = -1) => {
  return axiosGetRequest<MemberInfo[]>(
    `${getIdentityRoute()}/members/blocked/${numrecs}`
  );
};

export const getReferralLinkCall = () => {
  return axiosGetRequest<string>(`${getCommonRoute()}/referral/url`);
};

export const sendEmailsInviteCall = (emails: EmailsDto) => {
  return axiosPostRequest<EmailsDto, string>(
    `${getCommonRoute()}/referral/invitation`,
    emails
  );
};

export const registerByReferralLinkCall = (referralId: string) => {
  return axiosPatchRequest(
    `${getCommonRoute()}/referral/registered/${referralId}`
  );
};

export const registerByCampaignLinkCall = (campaignId: string) => {
  return axiosPostRequest(`${getCommonRoute()}/campaign/${campaignId}`);
};

export const resendVerificationEmailLinkCall = () => {
  return axiosPatchRequest(
    `${getIdentityRoute()}/member/verificationemail/resend`
  );
};

export const verifyEmailCall = (verificationData: VerifyEmailDto) => {
  return axiosOpenPatchRequest(
    `${getVerificationEmailIdentityRoute()}/member/verificationemail/verify`,
    verificationData
  );
};

export const sendQueryParamCampaignIdCall = (queryParamCampaignId: string) => {
  return axiosPostRequest(
    `${getCommonRoute()}/campaign/${queryParamCampaignId}`
  );
};

export const rejectMemberRequestCall = (memberId: string, roomId?: string) => {
  return axiosPatchRequest<void, Notification>(
    `${getIdentityRoute()}/room/${roomId}/member/${memberId}/reject`
  );
};

export const acceptMemberRequestCall = (memberId: string, roomId?: string) => {
  return axiosPatchRequest<void, Notification>(
    `${getIdentityRoute()}/room/${roomId}/member/${memberId}/accept`
  );
};
